import { Reference } from "@apollo/client";

import useMutationWithValidation from "@hooks/useMutationWithValidation";
import {
  ConsultReport,
  GetConsultDocument,
  GetConsultQuery,
  GetConsultQueryVariables,
  GetJourneyWithConsultsDocument,
  GetJourneyWithConsultsQuery,
  GetJourneyWithConsultsQueryVariables,
  InternalNoteDestroyDocument,
  InternalNoteDestroyMutation,
  InternalNoteDestroyMutationVariables
} from "@typing/Generated";

type Args = {
  consultId?: string | null;
  journeyId: string;
};

const useDestroyInternalNote = ({ consultId, journeyId }: Args) => {
  const [mutation] = useMutationWithValidation<InternalNoteDestroyMutation, InternalNoteDestroyMutationVariables>(
    InternalNoteDestroyDocument,
    "internalNote",
    {
      update: (store, { data }) => {
        //
        // Delete the note from the journey's full list of notes
        //
        try {
          store.modify({
            fields: {
              internalNotes(cachedRefs: readonly Reference[], { readField }) {
                return cachedRefs.filter(ref => data?.internalNoteDestroy?.id !== readField("id", ref));
              }
            },
            id: store.identify({ __typename: "Journey", id: journeyId })
          });
        } catch {}

        //
        // If there is a session report that includes the note, remove the note from its list
        //
        try {
          const cacheData = store.readQuery<GetJourneyWithConsultsQuery, GetJourneyWithConsultsQueryVariables>({
            query: GetJourneyWithConsultsDocument,
            variables: { id: journeyId }
          });
          if (cacheData?.journey.consults && data?.internalNoteDestroy?.id) {
            const consult = cacheData.journey.consults.find(
              consult =>
                consult.consultReport?.internalNotes &&
                consult.consultReport?.internalNotes?.findIndex(note => note.id === data.internalNoteDestroy?.id) >= 0
            );

            if (consult) {
              const notes = consult.consultReport?.internalNotes ?? [];
              const newNotes = notes.filter(note => note.id !== data.internalNoteDestroy?.id);

              const newConsults = [...cacheData.journey.consults].map(c => {
                if (c.id === consult.id) {
                  const consultReport = {
                    ...consult.consultReport,
                    internalNotes: newNotes
                  } as ConsultReport | null;

                  return {
                    ...consult,
                    consultReport
                  };
                }
                return c;
              });

              if (consult.consultReport) {
                const newCacheData = {
                  ...cacheData,
                  journey: {
                    ...cacheData.journey,
                    consults: newConsults
                  }
                };
                store.writeQuery<GetJourneyWithConsultsQuery, GetJourneyWithConsultsQueryVariables>({
                  data: newCacheData,
                  query: GetJourneyWithConsultsDocument
                });
              }
            }
          }
        } catch {
          // Trying to get the journey's consults will fail if we haven't loaded them already
        }

        //
        // If we asked for a consult directly then we need to remove it from that.
        //
        if (consultId) {
          try {
            const cacheData = store.readQuery<GetConsultQuery, GetConsultQueryVariables>({
              query: GetConsultDocument,
              variables: { id: consultId }
            });
            if (cacheData?.consult && data?.internalNoteDestroy?.id) {
              const consult = cacheData.consult;
              const notes = consult.consultReport?.internalNotes ?? [];

              const newNotes = notes.filter(note => note.id !== data.internalNoteDestroy?.id);

              if (consult.consultReport) {
                const newCacheData = {
                  ...cacheData,
                  consult: {
                    ...consult,
                    consultReport: {
                      ...consult.consultReport,
                      internalNotes: newNotes
                    }
                  }
                };
                store.writeQuery<GetConsultQuery, GetConsultQueryVariables>({
                  data: newCacheData,
                  query: GetConsultDocument
                });
              }
            }
          } catch {
            // Trying to run the query can fail
          }
        }
      }
    }
  );
  return mutation;
};

export default useDestroyInternalNote;
